import { BrowserRouter, Navigate, Routes, Route, Outlet } from 'react-router-dom';
import { useAuth } from './store';
import { canViewPage, HOME_PATH } from './constants';
import { LoginPage } from './pages/Login.page';
import { DashboardPage } from './pages/Dashboard.page';
import { ShiftsPage } from './pages/Shifts.page';
import { SchedulePage } from './pages/Schedule.page';
import { TimesheetsPage } from './pages/Timesheets.page';
import { AdminTablePage } from './pages/AdminTable.page';
import { StaffTablePage } from './pages/StaffTable.page';
import { TransactionsPage } from './pages/Transactions.page';
import { AppointmentsPage } from './pages/Appointments.page';
import { TipsPage } from './pages/Tips.page';
import { ReceivablesPage } from './pages/Receivables.page';
import { NeedsAttentionPage } from './pages/NeedsAttention.page';
import { AnalyticsPage } from './pages/Analytics.page';
import { NotifyListener } from './components/NotifyModal/NotifyListener';

// Blocks a page this role cannot reach, including by typing the URL. Hiding
// the nav link alone would not stop that, and the permission it reads is the
// same one the header reads — see PAGES_BY_ROLE in constants.
function RequirePage({ page }) {
  const { user } = useAuth();
  if (!canViewPage(user, page)) return <Navigate to={HOME_PATH} replace />;
  return <Outlet />;
}

// Routes that require an authenticated user; otherwise redirect to login.
// The NotifyListener polls for incoming notifications on every protected page.
function ProtectedRoute() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return (
    <>
      <NotifyListener />
      <Outlet />
    </>
  );
}

export function Router() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route element={<ProtectedRoute />}>
          <Route path="/dashboard" element={<DashboardPage />} />
          {/* Every page but the queue sits behind the permission it belongs
              to. The queue is the one page every role can open, so it is also
              where anybody bounced off a page they cannot see ends up. */}
          <Route element={<RequirePage page="shifts" />}>
            <Route path="/shifts" element={<ShiftsPage />} />
          </Route>
          <Route element={<RequirePage page="users" />}>
            <Route path="/admin" element={<AdminTablePage />} />
          </Route>
          <Route element={<RequirePage page="schedule" />}>
            <Route path="/schedule" element={<SchedulePage />} />
          </Route>
          <Route element={<RequirePage page="timesheets" />}>
            <Route path="/timesheets" element={<TimesheetsPage />} />
          </Route>
          <Route element={<RequirePage page="staff" />}>
            <Route path="/staff" element={<StaffTablePage />} />
          </Route>
          <Route element={<RequirePage page="appointments" />}>
            <Route path="/appointments" element={<AppointmentsPage />} />
          </Route>
          <Route element={<RequirePage page="payments" />}>
            <Route path="/transaction" element={<TransactionsPage />} />
          </Route>
          <Route element={<RequirePage page="attention" />}>
            <Route path="/attention" element={<NeedsAttentionPage />} />
          </Route>
          <Route element={<RequirePage page="tips" />}>
            <Route path="/reports/tips" element={<TipsPage />} />
          </Route>
          <Route element={<RequirePage page="receivables" />}>
            <Route path="/reports/receivables" element={<ReceivablesPage />} />
          </Route>
          <Route element={<RequirePage page="analytics" />}>
            <Route path="/reports/analytics" element={<AnalyticsPage />} />
          </Route>
        </Route>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
